import type { FC } from "react";

import {
  FEATURED_MODELS,
  type FeaturedModel,
  formatContextWindow,
  formatEffortRange,
  type ModelProvider,
  OTHER_MODEL_NAMES,
} from "./landingModels";
import {
  SubduedText,
  LandingSection,
  Panel,
  SectionHeading,
  MONO_LABEL_CLASS_NAME,
  Chip,
} from "./landingPrimitives";

const PROVIDER_STYLE: Record<ModelProvider, { dot: string; text: string }> = {
  Anthropic: { dot: "bg-orange-300", text: "text-orange-200" },
  OpenAI: { dot: "bg-emerald-300", text: "text-emerald-200" },
};

const SpecRow: FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) =>
  value ? (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className={`${MONO_LABEL_CLASS_NAME} text-[10px] text-slate-500`}>
        {label}
      </dt>
      <dd className="text-right text-sm text-slate-200">{value}</dd>
    </div>
  ) : null;

const FeaturedModelCard: FC<{ model: FeaturedModel }> = ({ model }) => {
  const style = PROVIDER_STYLE[model.provider];
  return (
    <Panel
      as="article"
      className="group flex flex-col overflow-hidden p-6 transition hover:border-slate-600"
    >
      <div className="relative flex h-6 items-center justify-between gap-3">
        <span
          className={`inline-flex items-center gap-2 ${MONO_LABEL_CLASS_NAME} ${style.text}`}
        >
          <span className={`h-1.5 w-1.5 ${style.dot}`} aria-hidden="true" />
          {model.provider}
        </span>
        {model.isNew ? <Chip square>New</Chip> : null}
      </div>
      <h3 className="relative mt-6 font-display text-2xl font-bold normal-case !tracking-tight text-white">
        {model.name}
      </h3>
      <p className="relative mt-3 flex-1 text-sm leading-6 text-slate-400">
        {model.blurb}
      </p>
      <dl className="relative mt-6 divide-y divide-slate-800 border-t border-slate-800">
        <SpecRow label="Context" value={formatContextWindow(model.ref)} />
        <SpecRow label="Reasoning" value={formatEffortRange(model.ref)} />
        <SpecRow label="Runs on" value={model.route} />
      </dl>
    </Panel>
  );
};

export const LandingModelsSection: FC = () => (
  <LandingSection id="models" band>
    <SectionHeading
      align="center"
      eyebrow="Models"
      title={
        <>
          The newest frontier models.{" "}
          <SubduedText>One pick per actor.</SubduedText>
        </>
      }
      body="Give each actor the model that suits its job, and switch when a better one ships. Memory and identity stay with the actor, not the model."
    />
    <div className="mt-14 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {FEATURED_MODELS.map((model) => (
        <FeaturedModelCard key={model.ref} model={model} />
      ))}
    </div>
    <div className="mt-10 flex flex-col items-center gap-4">
      <p className={`${MONO_LABEL_CLASS_NAME} text-slate-500`}>
        Also on the roster
      </p>
      <ul className="flex max-w-4xl flex-wrap justify-center gap-2">
        {OTHER_MODEL_NAMES.map((name) => (
          <li
            key={name}
            className="border border-slate-700 bg-slate-900/60 px-3 py-1 text-xs text-slate-300"
          >
            {name}
          </li>
        ))}
      </ul>
    </div>
  </LandingSection>
);
